import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useStoreActions } from 'easy-peasy';
import Modal from './Modal';
import { httpGetMines, httpGetMineShafts } from 'services/mines';
import { httpGetPool } from 'services/pools';
import './EditPumpModal.scss'; // Use the same styles

export default function EditPoolModal({ isOpen, poolId, initialData, onClose, onSuccess }) {
  const { t } = useTranslation();
  const { updatePool } = useStoreActions((actions) => actions.pools);
  const [formData, setFormData] = useState({
    name: '',
    mine: '',
    mine_shaft: ''
  });
  const [mines, setMines] = useState([]);
  const [mineShafts, setMineShafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingShafts, setLoadingShafts] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen || !poolId) return;

    const fetchData = async () => {
      try {
        // If we have initialData, no need for loading state!
        if (initialData) {
          setLoading(false);
          setFormData({
            name: initialData.name,
            mine: initialData.shaft?.mine?.id || '',
            mine_shaft: initialData.shaft?.id || ''
          });
        } else {
          setLoading(true);
        }
        
        setError(null);
        
        // Load mines in background (doesn't block UI if we have initialData)
        const minesRes = await httpGetMines();
        const minesData = Array.isArray(minesRes.data) ? minesRes.data : minesRes.data.results || [];
        setMines(minesData);

        // Load mine shafts based on initialData or fetched pool data
        if (initialData) {
          if (initialData.shaft?.mine?.id) {
            const shaftsRes = await httpGetMineShafts(initialData.shaft.mine.id);
            const shaftsData = Array.isArray(shaftsRes.data) ? shaftsRes.data : shaftsRes.data.results || [];
            setMineShafts(shaftsData);
          }
        } else {
          // Only fetch pool data if no initialData
          const poolRes = await httpGetPool(poolId);
          const pool = poolRes.data;
          
          setFormData({
            name: pool.name,
            mine: pool.shaft?.mine?.id || '',
            mine_shaft: pool.shaft?.id || ''
          });
          
          if (pool.shaft?.mine?.id) {
            const shaftsRes = await httpGetMineShafts(pool.shaft.mine.id);
            const shaftsData = Array.isArray(shaftsRes.data) ? shaftsRes.data : shaftsRes.data.results || [];
            setMineShafts(shaftsData);
          }
          setLoading(false);
        }
      } catch (err) {
        console.error('Error loading data:', err);
        setError(t('pool_modal_load_error'));
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- t only for error strings; omit to avoid refetch on language change
  }, [isOpen, poolId, initialData]);

  const handleMineChange = async (mineId) => {
    setFormData(prev => ({ ...prev, mine: mineId, mine_shaft: '' }));
    setMineShafts([]);
    
    if (mineId) {
      setLoadingShafts(true);
      try {
        const response = await httpGetMineShafts(mineId);
        const shaftsData = Array.isArray(response.data) ? response.data : response.data.results || [];
        setMineShafts(shaftsData);
      } catch (err) {
        console.error('Error fetching mine shafts:', err);
        setError(t('pool_modal_shafts_error'));
        setMineShafts([]);
      } finally {
        setLoadingShafts(false);
      }
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const submitData = {
        name: formData.name,
        shaft: parseInt(formData.mine_shaft)
      };

      // Use store action to update pool - updates store automatically
      const updatedPool = await updatePool({ id: poolId, data: submitData });
      
      if (updatedPool) {
        if (onSuccess) {
          onSuccess(updatedPool);
        }
        onClose();
      } else {
        setError(t('pool_modal_update_failed'));
      }
    } catch (err) {
      console.error('Error updating pool:', err);
      setError(err.response?.data?.message || t('pool_modal_update_failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('pool_modal_edit_title')} size="medium">
      {loading ? (
        <div className="text-center py-5">{t('ui_loading')}</div>
      ) : (
        <form className="edit-modal-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">{t('pool_modal_name_required')}</label>
            <input
              className="form-input"
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder={t('pool_modal_placeholder_name')}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('pool_modal_mine')} *</label>
            <select
              className="form-select"
              value={formData.mine}
              onChange={(e) => handleMineChange(e.target.value)}
              required
            >
              <option value="">{t('pool_modal_select_mine')}</option>
              {mines.map(mine => (
                <option key={mine.id} value={mine.id}>
                  {mine.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">{t('pool_modal_shaft')} *</label>
            <select
              className="form-select"
              value={formData.mine_shaft}
              onChange={(e) => handleInputChange('mine_shaft', e.target.value)}
              disabled={!formData.mine || loadingShafts}
              required
            >
              <option value="">{t('pool_modal_select_shaft')}</option>
              {loadingShafts && <option>{t('ui_loading')}</option>}
              {!loadingShafts && mineShafts.map(shaft => (
                <option key={shaft.id} value={shaft.id}>
                  {shaft.name}
                </option>
              ))}
            </select>
          </div>

          {error && (
            <div className="form-error">
              {error}
            </div>
          )}

          <div className="form-actions">
            <button 
              type="button" 
              className="btn-secondary" 
              onClick={onClose}
              disabled={saving}
            >
              {t('cancel')}
            </button>
            <button 
              type="submit" 
              className="btn-primary"
              disabled={saving || !formData.name || !formData.mine_shaft}
            >
              {saving ? t('ui_saving') : t('Save')}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}

